'use client'

import { keetaIntegratorApi } from 'src/lib/axios'
import Cart from '../../../types/cart'
import { useContext, useState } from 'react'
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalTitle,
  Form,
  FormSelect,
} from 'react-bootstrap'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { CartsContext } from '@context/cart.ctx'

interface KeetaOrderModalProps {
  onAccept?: () => void
  onReject?: () => void
}

type KeetaReasonTypes =
  | 'INSUFFICIENT_DISHES'
  | 'SUSPENSION_OF_BUSINESS'
  | 'INSUFFICIENT_MANPOWER'
  | 'OTHERS'

const keetaReasonTranslations: Record<KeetaReasonTypes, string> = {
  INSUFFICIENT_DISHES: 'Pratos insuficientes',
  SUSPENSION_OF_BUSINESS: 'Suspensão do negócio',
  INSUFFICIENT_MANPOWER: 'Mão de obra insuficiente',
  OTHERS: 'Outros',
}

const cancelOrderSchema = z.object({
  reason: z.string().min(10, 'O motivo deve ter pelo menos 10 caracteres'),
  reasonType: z.enum([
    'INSUFFICIENT_DISHES',
    'SUSPENSION_OF_BUSINESS',
    'INSUFFICIENT_MANPOWER',
    'OTHERS',
  ]),
})

type CancelOrderFormData = z.infer<typeof cancelOrderSchema>

export const KeetaOrderModal = ({
  onAccept,
  onReject,
}: KeetaOrderModalProps) => {
  const { keetaOrders, setKeetaOrders } = useContext(CartsContext)
  const [showCancelModal, setShowCancelModal] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = useForm<CancelOrderFormData>({
    resolver: zodResolver(cancelOrderSchema),
    defaultValues: {
      reason: '',
      reasonType: 'INSUFFICIENT_DISHES',
    },
  })

  const cart = keetaOrders.filter((o) => o.status === null)[0]

  console.log(cart);


  // Não renderiza o modal se não houver cart
  if (!cart) return null

  const handleCancelOrder = async (data: CancelOrderFormData, cart: Cart) => {
    try {
      await keetaIntegratorApi.post(`orders/update/${cart.orderId}`, {
        dispatchType: 'CANCELLED',
        reason: data.reason,
        code: data.reasonType,
        mode: 'MANUALLY',
      })
      handleUpdateKeeta({ status: 'canceled' })
      setShowCancelModal(false)
      reset()
      onReject?.()
    } catch (error) {
      throw error
    }
  }

  const handleOpenCancelModal = () => {
    setShowCancelModal(true)
  }

  const handleCloseCancelModal = () => {
    setShowCancelModal(false)
    reset()
  }

  const handleConfirmOrder = async (cart: Cart) => {
    try {
      await keetaIntegratorApi.post(`orders/update/${cart.orderId}`, {
        dispatchType: 'CONFIRMED',
        reason: 'Pedido confirmado pelo Dashboard',
        createdAt: new Date().toISOString(),
        orderExternalCode: cart.orderId,
        preparationTime: 30,
      })
      handleUpdateKeeta({ status: 'production' })
      onAccept?.()
    } catch (error) {
      throw error
    }
  }

  const handleUpdateKeeta = ({ status }: { status: Cart['status'] }) => {
    setKeetaOrders((prev) =>
      prev.map((order) => {
        if (order.id === cart.id) {
          order.status = status
        }
        return order
      })
    )
  }

  let cartType = 'Delivery'

  switch (cart.type) {
    case 'D':
      cartType = 'Delivery'
      break
    case 'P':
      cartType = 'Encomenda'
      break
    case 'T':
      cartType = 'Mesa'
      break
    default:
      cartType = 'Delivery'
  }

  return (
    <Modal
      show
      onHide={() => { }} // Função vazia para impedir fechamento
      centered
      backdrop="static"
      keyboard={false}
      size="lg"
    >
      <ModalHeader>
        <ModalTitle className="fw-bold">Pedido Keeta</ModalTitle>
      </ModalHeader>

      <ModalBody className="p-4">
        {/* Order Details */}
        <div className="mb-4">
          <div className="row mb-2">
            <div className="col-6">
              <span className="text-muted fw-medium">Cód. Pedido:</span>
            </div>
            <div className="col-6 text-end">
              <span className="fw-semibold">{cart?.id}</span>
            </div>
          </div>

          <div className="row mb-2">
            <div className="col-6">
              <span className="text-muted fw-medium">Nome:</span>
            </div>
            <div className="col-6 text-end">
              <span className="fw-semibold">{cart?.client?.name}</span>
            </div>
          </div>

          <div className="row mb-2">
            <div className="col-6">
              <span className="text-muted fw-medium">Total:</span>
            </div>
            <div className="col-6 text-end">
              <span className="fw-semibold">
                R$ {cart?.total.toFixed(2)}
                {/* • Troco: {cart?.change?.toFixed(2)} */}
              </span>
            </div>
          </div>

          <div className="row mb-2">
            <div className="col-6">
              <span className="text-muted fw-medium">Pagamento:</span>
            </div>
            <div className="col-6 text-end">
              <span className="fw-semibold">{cart?.formsPayment[0].label}</span>
            </div>
          </div>

          <div className="row mb-2">
            <div className="col-6">
              <span className="text-muted fw-medium">Tipo:</span>
            </div>
            <div className="col-6 text-end">
              <span className="fw-semibold">{cartType}</span>
            </div>
          </div>
        </div>

        {/* Items Section */}
        <div className="border-top mb-3 pt-3">
          <div className="row mb-3">
            <div className="col-6">
              <h6 className="fw-semibold mb-0">Itens</h6>
            </div>
            <div className="col-6 text-end">
              <h6 className="fw-semibold mb-0">Preços</h6>
            </div>
          </div>

          {cart?.itens.map((item: any, index: number) => (
            <div key={index} className="mb-3">
              <div className="row">
                <div className="col-8">
                  <p className="fw-medium mb-1">{item.name}</p>
                  {item.obs && <p className="text-muted small mb-0 ms-3 my-0 p-0">• {item.obs}</p>}
                </div>
                <div className="col-4 text-end">
                  <span className="fw-semibold">
                    {item.details.value.toFixed(2)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Observation */}
        {cart?.obs && (
          <div className="border-top pt-3">
            <p className="small text-muted mb-0">
              <span className="fw-medium">Obs.:</span> {cart.obs}
            </p>
          </div>
        )}
      </ModalBody>

      <ModalFooter className="border-top">
        <div className="d-flex w-100 gap-2">
          <button
            onClick={handleOpenCancelModal}
            className="btn btn-outline-danger flex-fill fw-semibold"
          >
            Recusar Pedido
          </button>
          <button
            onClick={() => handleConfirmOrder(cart!)}
            className="btn btn-primary flex-fill fw-semibold"
          >
            Aceitar Pedido
          </button>
        </div>
      </ModalFooter>

      {/* Modal de Confirmação de Cancelamento */}
      <Modal
        show={showCancelModal}
        onHide={handleCloseCancelModal}
        centered
        backdrop="static"
        keyboard={false}
      >
        <ModalHeader>
          <ModalTitle className="fw-bold">Confirmar Cancelamento</ModalTitle>
        </ModalHeader>

        <ModalBody className="p-4">
          <p className="mb-3">Preencha os dados para cancelar o pedido:</p>

          <Form
            id="cancel-order-form"
            onSubmit={handleSubmit((data) => handleCancelOrder(data, cart!))}
          >
            <div className="mb-3">
              <label htmlFor="reasonType" className="form-label fw-medium">
                Tipo de motivo:
              </label>
              <FormSelect
                {...register('reasonType')}
                className={errors.reasonType ? 'is-invalid' : ''}
              >
                {Object.entries(keetaReasonTranslations).map(([key, value]) => (
                  <option key={key} value={key}>
                    {value}
                  </option>
                ))}
              </FormSelect>
              {errors.reasonType && (
                <div className="invalid-feedback">
                  {errors.reasonType.message}
                </div>
              )}
            </div>

            <div className="mb-3">
              <label htmlFor="reason" className="form-label fw-medium">
                Motivo detalhado:
              </label>
              <textarea
                {...register('reason')}
                className={`form-control ${errors.reason ? 'is-invalid' : ''}`}
                rows={4}
                placeholder="Descreva o motivo do cancelamento (mínimo 10 caracteres)..."
              />
              {errors.reason && (
                <div className="invalid-feedback">{errors.reason.message}</div>
              )}
              <div className="form-text">
                {watch('reason')?.length || 0}/10 caracteres mínimos
              </div>
            </div>

            <div className="alert alert-warning">
              <small>
                <strong>Atenção:</strong> Esta ação não pode ser desfeita. O
                pedido será cancelado permanentemente.
              </small>
            </div>
          </Form>
        </ModalBody>

        <ModalFooter className="border-top">
          <div className="d-flex w-100 gap-2">
            <button
              type="button"
              onClick={handleCloseCancelModal}
              className="btn btn-outline-secondary flex-fill fw-semibold"
            >
              Cancelar
            </button>
            <button
              type="submit"
              form="cancel-order-form"
              className="btn btn-danger flex-fill fw-semibold"
            >
              Confirmar Cancelamento
            </button>
          </div>
        </ModalFooter>
      </Modal>
    </Modal>
  )
}
